/**
 * #1602 — "Review cover · N" — the lead's in-app signal that a member
 * reported off sick (MC) on days they hold PUBLISHED shifts in this cycle.
 *
 * A member's MC lands as a Leave row + audit and nothing else; before this
 * chip the lead had to notice unaided. The chip self-gates (lead + `mc_recover` component +
 * count > 0 — it renders NOTHING otherwise, so the workspace top region is
 * byte-identical for everyone else) and stays deliberately small: the focal
 * element of the workspace is the roster, and the LIST lives inside the
 * card this chip opens, not on the page.
 *
 * The chip is the INBOX, not a remedy. The remedy is the call-back ladder
 * on the day itself ("Short · options"): one place where a short cell is
 * covered, whoever noticed it. So the primary action per (member, day) is
 * "Short · options for <day>" — it arms the one-shot
 * `shortfallOpenRequest` store and switches back to the roster, where the
 * grid opens that day's ladder. An item leaves this list the moment its
 * seats stop being short — by the ladder, a swap, a hand edit, anything —
 * so the two reads never disagree (`still_short` names what remains).
 *
 * The inbox carries a SECOND kind of row: **rest owed after a recall**.
 * The ladder offers the day back on the recall's own date, but every door
 * into the ladder — the grid/list/phone "Short · options" chips — is gated
 * on the day still being short. So a recall that CLOSED the day's gap left
 * the rest it owed with no door at all: the day read "Fully covered", the
 * lead closed the panel, and the recalled person kept working the morning
 * they were owed. These rows are that door, with the same primary action
 * ("Short · options for <recall day>") onto the same ladder, which still
 * renders its rest card on a day where nothing is short.
 *
 * The ladder is the ONE path for covering a day (#1952): the old
 * "Reshuffle wards (advanced)" route into the Last-minute cover cascade is
 * gone. Each item's ghost *Fix and re-solve* (#1960) opens the
 * minimal-change re-solve in the right-panel overlay, the absence pinned off.
 * "Ask the team" sends cover asks; "Dismiss" is the quiet two-step opt-out
 * for MCs the lead chooses not to act on; the backend records it as an
 * audit row, so it is durable + auditable.
 *
 * Radix-free markup (plain toggle + absolute card) keeps the DOM asserts
 * in tests direct.
 */
import { Stethoscope } from "lucide-react";
import { useState } from "react";

import {
  type McAttentionItem,
  type McAttentionRestOwed,
  type McCoverBroadcastResponse,
  useDismissMcAttention,
  useMcAttention,
  useMcCoverBroadcast,
} from "../../api/useMcAttention";
import { useTeamFeature } from "../../api/useTeamFeature";
import { isHeldResolveRefusal } from "../../lib/heldResolve";
import { roleDisplay } from "../../memberapp/calendar/calendarModel";
import { useShortfallOpenRequestStore } from "../../store/shortfallOpenRequest";
import { HeldResolveDiscard } from "../HeldResolveDiscard";
import { FixAndResolveButton } from "../repair/FixAndResolveButton";
import { Badge, Button } from "../ui";
import { coverAskDefaultSelection, coverAskSeatIsShort } from "./coverAskSeats";

function formatShort(iso: string): string {
  const dt = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/** The days this item actually needs cover ON: the dates of its published
 *  shifts, falling back to the MC window when the rows aren't served. */
function affectedDays(item: McAttentionItem): string[] {
  const fromRows = (item.affected_assignments ?? []).map((a) => a.date);
  const uniq = Array.from(new Set(fromRows.length > 0 ? fromRows : item.dates));
  uniq.sort();
  return uniq;
}

/** `55 · AM` — one seat as the lead names it, out of the PERSISTED role
 *  token the attention payload carries (`STATION_55_AM`, `ONCALL_NIGHT`).
 *
 *  The picker printed the token verbatim, so the lead was asked to tick
 *  `Wed 4 Nov · STATION_55_AM` — a database string in a question about a
 *  ward. `roleDisplay` is the same resolver the member app's calendar reads
 *  its shift labels with; it already knows the solver's role grammar
 *  (a station name may itself contain `_`, a full day is not a session). */
export function seatLabel(role: string): string {
  const display = roleDisplay(role);
  return display.session ? `${display.station} · ${display.session}` : display.station;
}

/** `· recalled Mon 2 Nov · 1 rest day owed · can be given Tue 3 Nov back` —
 *  the rest-owed row's sentence. The separators are TEXT, not layout (the
 *  same fix the MC row needed, #1909). The ladder is opened on the RECALL
 *  day — that is where it serves its rest card — so the recall day is named
 *  first and the day being offered back last, in the order the lead reads
 *  them. */
function restLine(rest: McAttentionRestOwed): string {
  const owed = `${rest.days_owed} rest day${rest.days_owed === 1 ? "" : "s"} owed`;
  return `· recalled ${formatShort(rest.recall_date)} · ${owed} · can be given ${formatShort(
    rest.rest_date,
  )} back`;
}

function formatDates(dates: string[]): string {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first === undefined || last === undefined) return "";
  if (dates.length === 1) return formatShort(first);
  return `${formatShort(first)} – ${formatShort(last)}`;
}

export interface McAttentionChipSlotProps {
  cycleId: string;
  teamId: string;
  isLead: boolean;
  /** Returns to the roster surface (closing whatever modal/drawer is up),
   *  where the grid opens the armed day's "Short · options" ladder. The
   *  roster is always mounted, so this only has to clear the surface. */
  onOpenRoster: () => void;
}

export function McAttentionChipSlot({
  cycleId,
  teamId,
  isLead,
  onOpenRoster,
}: McAttentionChipSlotProps) {
  // Gated on the `mc_recover` component (Sick cover).
  // `undefined` (loading) renders nothing so the chip doesn't flash in.
  const mcRecoverOn = useTeamFeature(teamId, "mc_recover");
  const attentionQuery = useMcAttention(cycleId, Boolean(isLead && mcRecoverOn === true));
  const dismiss = useDismissMcAttention(cycleId);
  const broadcast = useMcCoverBroadcast(cycleId);
  const requestShortfallOptions = useShortfallOpenRequestStore((s) => s.requestShortfallOptions);

  const [open, setOpen] = useState(false);
  // Two-step dismiss: first click arms the confirm on ONE item.
  const [confirmLeaveId, setConfirmLeaveId] = useState<string | null>(null);
  // #1603 — "Ask the team" opens an inline picker on ONE item: the
  // affected shifts, default all checked, then Send.
  const [askLeaveId, setAskLeaveId] = useState<string | null>(null);
  const [askSelection, setAskSelection] = useState<Set<string>>(new Set());
  const [askResult, setAskResult] = useState<McCoverBroadcastResponse | null>(null);

  const items = attentionQuery.data?.items ?? [];
  const restOwed = attentionQuery.data?.rest_owed ?? [];
  // One count over both kinds: the chip is the inbox of everything left to
  // settle on this cycle's cover, and it self-hides when nothing is.
  const total = items.length + restOwed.length;
  if (!isLead || mcRecoverOn !== true || total === 0) return null;

  // The one path: hand the DAY to the roster grid, which opens that
  // day's call-back ladder. One-shot — the grid clears the request.
  const openLadder = (iso: string) => {
    requestShortfallOptions({ cycleId, date: iso });
    setOpen(false);
    onOpenRoster();
  };

  const onDismissClick = (item: McAttentionItem) => {
    if (confirmLeaveId !== item.leave_id) {
      setConfirmLeaveId(item.leave_id);
      return;
    }
    setConfirmLeaveId(null);
    dismiss.mutate({ membership_id: item.membership_id, dates: item.dates });
  };

  // #1603 — first click opens the shift picker; Send fires the broadcast and
  // shows created/skipped counts inline. #99 — the picker starts with only
  // the seats the ladder still calls short checked: an ask for a seat that is
  // already covered is noise in five people's feeds, and the accepted ask on
  // its neighbour does not retract it.
  const onAskClick = (item: McAttentionItem) => {
    setAskResult(null);
    if (askLeaveId === item.leave_id) {
      setAskLeaveId(null);
      return;
    }
    setAskLeaveId(item.leave_id);
    setAskSelection(
      new Set(coverAskDefaultSelection(item.affected_assignments ?? [], item.still_short)),
    );
  };

  const onAskSend = (item: McAttentionItem) => {
    const rows = item.affected_assignments ?? [];
    const all = rows.map((a) => a.assignment_id);
    // #99 — the covered seats are un-checkable in the picker; belt-and-braces,
    // they are also filtered out of the send.
    const chosen = rows
      .filter((a) => askSelection.has(a.assignment_id) && coverAskSeatIsShort(a, item.still_short))
      .map((a) => a.assignment_id);
    broadcast.mutate(
      {
        mc_membership_id: item.membership_id,
        dates: item.dates,
        // Omit the subset when everything is selected — "default all".
        ...(chosen.length > 0 && chosen.length < all.length ? { assignment_ids: chosen } : {}),
      },
      {
        onSuccess: (res) => {
          setAskResult(res);
          setAskLeaveId(null);
        },
      },
    );
  };

  return (
    <div className="relative" data-testid="mc-attention-slot">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((o) => !o)}
        data-testid="mc-attention-chip"
        data-tour="review-cover"
        aria-label={`Review cover — ${total} thing${total === 1 ? "" : "s"} still to settle on the cover for these dates`}
        aria-expanded={open}
        // #1922 — the same pressed look as the roster's surface chips while
        // its card is open (the toggle state is `aria-expanded`).
        className={open ? "border-accent bg-accent-soft/50 hover:bg-accent-soft/70" : undefined}
      >
        <Stethoscope className="h-4 w-4" aria-hidden="true" />
        Review cover
        <Badge tone="warning" className="ml-1" data-testid="mc-attention-count">
          {total}
        </Badge>
      </Button>

      {open ? (
        <div
          className="absolute right-0 top-full z-40 mt-2 w-96 max-w-[90vw] rounded-lg border border-border bg-bg-elevated p-4 shadow-lg"
          data-testid="mc-attention-list"
          aria-label="Cover still to settle on these dates"
        >
          {items.length > 0 ? (
            <>
              <p className="text-sm font-semibold text-fg">Off sick on published days</p>
              <p className="mt-1 text-xs text-fg-muted">
                Off sick on published shifts. Seats still short.
              </p>
            </>
          ) : null}
          <ul className="mt-3 flex flex-col gap-3">
            {items.map((item) => (
              <li
                key={item.leave_id}
                className="flex flex-col gap-2 border-t border-border pt-3 first:border-t-0 first:pt-0"
                data-testid="mc-attention-item"
              >
                <div className="text-sm text-fg">
                  <span className="font-medium">{item.display_name}</span>{" "}
                  <span className="text-fg-muted">
                    · {formatDates(item.dates)} · {item.affected_assignment_count} published shift
                    {item.affected_assignment_count === 1 ? "" : "s"}
                  </span>
                  {/* The separators are TEXT, not layout: without the explicit
                      spaces the spans butt together and the row read
                      "1 published shift· 55 AM still shortCover ask out · 2". */}
                  {(item.still_short ?? []).length > 0 ? (
                    <>
                      {" "}
                      <span className="text-fg-muted" data-testid="mc-attention-still-short">
                        · {(item.still_short ?? []).join(", ")} still short
                      </span>
                    </>
                  ) : null}{" "}
                  {(item.open_cover_asks ?? 0) > 0 ? (
                    // #1603 — cover asks are already out with the team:
                    // in progress, not unhandled.
                    <Badge tone="accent" className="ml-2" data-testid="mc-attention-in-progress">
                      Cover ask out · {item.open_cover_asks}
                    </Badge>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Primary — one button per affected day, straight to that
                      day's call-back ladder. */}
                  {affectedDays(item).map((iso) => (
                    <Button
                      key={iso}
                      size="sm"
                      onClick={() => openLadder(iso)}
                      data-testid={`mc-attention-open-ladder-${iso}`}
                    >
                      Short · options for {formatShort(iso)}
                    </Button>
                  ))}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={broadcast.isPending}
                    onClick={() => onAskClick(item)}
                    data-testid="mc-attention-ask"
                  >
                    Ask the team
                  </Button>
                  <FixAndResolveButton
                    teamId={teamId}
                    cycleId={cycleId}
                    date={affectedDays(item)[0] ?? item.dates[0] ?? ""}
                    absent={[
                      {
                        membership_id: item.membership_id,
                        display_name: item.display_name,
                        dates: item.dates,
                      },
                    ]}
                    onOpened={() => {
                      setOpen(false);
                      onOpenRoster();
                    }}
                    testid="mc-attention-fix-resolve"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={dismiss.isPending}
                    onClick={() => onDismissClick(item)}
                    data-testid="mc-attention-dismiss"
                  >
                    {confirmLeaveId === item.leave_id ? "Confirm dismiss" : "Dismiss"}
                  </Button>
                </div>
                {askLeaveId === item.leave_id ? (
                  <div
                    className="flex flex-col gap-1.5 rounded-md border border-border bg-bg-subtle p-2"
                    data-testid="mc-attention-ask-picker"
                  >
                    <p className="text-xs text-fg-muted">
                      Everyone eligible gets the ask in their swaps feed; the first to accept covers
                      the shift. {item.display_name.split(" ")[0] ?? "The member"} isn't named to
                      teammates.
                    </p>
                    {(item.affected_assignments ?? []).map((a) => {
                      // #99 — a seat the ladder no longer calls short is
                      // shown (the lead should see the whole MC) but cannot
                      // be asked for: no ask is ever created for a covered
                      // seat.
                      const stillShort = coverAskSeatIsShort(a, item.still_short);
                      return (
                        <label
                          key={a.assignment_id}
                          className={
                            stillShort
                              ? "flex items-center gap-2 text-xs text-fg"
                              : "flex items-center gap-2 text-xs text-fg-faint"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={stillShort && askSelection.has(a.assignment_id)}
                            disabled={!stillShort}
                            onChange={(e) => {
                              setAskSelection((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(a.assignment_id);
                                else next.delete(a.assignment_id);
                                return next;
                              });
                            }}
                            data-testid={`mc-attention-ask-shift-${a.assignment_id}`}
                          />
                          <span>
                            {formatShort(a.date)} · {seatLabel(a.role)}
                            {stillShort ? "" : " · already covered"}
                          </span>
                        </label>
                      );
                    })}
                    <div>
                      <Button
                        size="sm"
                        disabled={broadcast.isPending || askSelection.size === 0}
                        onClick={() => onAskSend(item)}
                        data-testid="mc-attention-ask-send"
                      >
                        {broadcast.isPending ? "Sending…" : "Send cover asks"}
                      </Button>
                    </div>
                    {broadcast.isError ? (
                      isHeldResolveRefusal(broadcast.error) ? (
                        // #2263 — a held re-solve changes this day: its way
                        // out, then send the asks again.
                        <>
                          <p role="alert" className="text-xs text-danger">
                            {broadcast.error.detail}
                          </p>
                          <HeldResolveDiscard
                            cycleId={cycleId}
                            error={broadcast.error}
                            then="Send the asks again."
                          />
                        </>
                      ) : (
                        <p role="alert" className="text-xs text-danger">
                          Couldn't send the asks — try again.
                        </p>
                      )
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          {restOwed.length > 0 ? (
            <div
              className={items.length > 0 ? "mt-4 border-t border-border pt-3" : undefined}
              data-testid="mc-attention-rest-section"
            >
              <p className="text-sm font-semibold text-fg">Rest owed after a recall</p>
              <p className="mt-1 text-xs text-fg-muted">
                Called back from post-call rest. Still owed the day.
              </p>
              <ul className="mt-3 flex flex-col gap-3">
                {restOwed.map((rest) => (
                  <li
                    key={`${rest.membership_id}-${rest.recall_date}`}
                    className="flex flex-col gap-2 border-t border-border pt-3 first:border-t-0 first:pt-0"
                    data-testid="mc-attention-rest-item"
                  >
                    <div className="text-sm text-fg">
                      <span className="font-medium">{rest.display_name}</span>{" "}
                      <span className="text-fg-muted">{restLine(rest)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => openLadder(rest.recall_date)}
                        data-testid={`mc-attention-open-ladder-${rest.recall_date}`}
                      >
                        Short · options for {formatShort(rest.recall_date)}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {askResult ? (
            <p className="mt-3 text-xs text-fg-muted" data-testid="mc-attention-ask-result">
              {askResult.created.length} cover ask{askResult.created.length === 1 ? "" : "s"} sent
              {askResult.skipped.length > 0
                ? ` · ${askResult.skipped.length} skipped (already asked or in the past)`
                : ""}
              .
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
